import { LocationHelper } from './location-helper';

export type HostAndPort = 'on-prem' | string | undefined;

export interface ApiLocation {
    hostAndPort: HostAndPort;
    rootUri: string;
}

export interface ApiConfiguration {
    ddvMWLocation: ApiLocation;
    monitorMWLocation: ApiLocation;
    ddvApiLocation: ApiLocation;
    monitorApiLocation: ApiLocation;
    flagsApiLocation: ApiLocation;
    trebekApiLocation: ApiLocation;
    wauthApiLocation: ApiLocation;
    xtlkApiLocation: ApiLocation;
    pscLocation: ApiLocation;
    nqsApiLocation: ApiLocation;
    cfbApiLocation: ApiLocation;
    reverseProxyLocation: ApiLocation;
    production: boolean;
    onPremApiHost: string;
    replaceHostInMetadataAndQueryURLs: boolean;
    replaceHostInActionsURL: boolean;
    removeRestFromActionPath: boolean;
    xtlkManagementLocation: string;
}

export const defaultApiConfiguration: ApiConfiguration = {
    ddvMWLocation: {
        hostAndPort: undefined,
        rootUri: './mw/api/',
    },
    monitorMWLocation: {
        hostAndPort: undefined,
        rootUri: './mw/',
    },
    ddvApiLocation: {
        hostAndPort: undefined,
        rootUri: './api/',
    },
    monitorApiLocation: {
        hostAndPort: undefined,
        rootUri: './api/',
    },
    trebekApiLocation: {
        hostAndPort: undefined,
        rootUri: '/trebek/api/rest/',
    },
    flagsApiLocation: {
        hostAndPort: undefined,
        rootUri: '/featureflags/api/',
    },
    wauthApiLocation: {
        hostAndPort: undefined,
        rootUri: '/wauth/api/',
    },
    xtlkApiLocation: {
        hostAndPort: undefined,
        rootUri: '/crosstalk/api/',
    },
    pscLocation: {
        hostAndPort: undefined,
        rootUri: '/psc/',
    },
    nqsApiLocation: {
        hostAndPort: undefined,
        rootUri: '/named-query/api/',
    },
    cfbApiLocation: {
        hostAndPort: undefined,
        rootUri: '/field-builder/api/',
    },
    reverseProxyLocation: {
        hostAndPort: undefined,
        rootUri: '/trebek/api/reverse-proxy/rr/',
    },
    onPremApiHost: 'localhost:8100',
    production: false,
    replaceHostInMetadataAndQueryURLs: true,
    replaceHostInActionsURL: true,
    removeRestFromActionPath: true,
    xtlkManagementLocation: '',
};

export function apiRootForService(service: keyof ApiConfiguration, config: ApiConfiguration): string {
    const location: ApiLocation = config[service] as ApiLocation;
    const hostAndPort = hostAndPortForService(location.hostAndPort, config.onPremApiHost);

    // regex to remove // or /// from the URI
    const hostAndPortAndPath = `${hostAndPort}/${location.rootUri}`.replace(/\/{2,}/g, '/');
    return `${LocationHelper.getProtocol()}//${hostAndPortAndPath}`;
}

export function hostAndPortForService(hostAndPort: HostAndPort, onPremApiHost: string): string {
    return hostAndPort === 'on-prem' ? onPremApiHost : (hostAndPort ?? LocationHelper.getHost());
}

export const legacyUatDomain = 'hsuat.com';
export const uatDomain = 'hsftuat.com';
export const legacyProdDomain = 'hedgeserv.com';
export const prodDomain = 'hsft.com';

const legacyRndDomain = 'poc-rnd.hsuat.com'; // when we move to the new domain, this will be 'poc-rnd.hsftuat.com' and should be renamed

export function getApmEnvironment(): string {
    const hostname = LocationHelper.getHostname();
    const pathname = LocationHelper.getPathname();

    if (hostname.includes(legacyUatDomain) || hostname.includes(uatDomain)) {
        return pathname.startsWith('/ddv-edge') || hostname.includes('poc-rnd') ? 'edge' : 'uat';
    } else if (hostname.includes('access-dr')) {
        return 'dr';
    } else {
        return 'prod';
    }
}

export function getOnPremApiHost(): string {
    const hostname = LocationHelper.getHostname();

    if (hostname.includes(legacyUatDomain) || hostname.includes(uatDomain)) {
        const domain = hostname.includes(legacyUatDomain) ? legacyUatDomain : uatDomain;
        return `access.${domain}`;
    }

    const domain = hostname.includes(legacyProdDomain) ? legacyProdDomain : prodDomain;
    return hostname.includes('access-dr') ? `access-dr.${domain}` : `access.${domain}`;
}

export function getNQSApiHost(): string {
    return getAwsApiHost('named-query', 'on-prem');
}

export function getCFBApiHost(): string {
    return getAwsApiHost('field-builder', 'on-prem');
}

export function getPSCHostAndPort(): string {
    return getAwsApiHost('psc', 'on-prem');
}

export function getDDVApiHost(): string {
    return getAwsApiHost('ddv', 'on-prem');
}

export function getFeatureFlagsApiHost(): string {
    return getAwsApiHost('featureflags', 'on-prem');
}

export function getTrebekApiHost(): string {
    const app = 'trebek';
    return getAwsApiHost(app, `${app}.${legacyRndDomain}`);
}

export function getNQSHostForManageNamedQueriesLink(): string {
    const app = 'named-query';
    const fallback = !defaultApiConfiguration.nqsApiLocation.rootUri.includes('nqs/api') ?
        `${app}.${legacyRndDomain}` :
        'localhost:8300';
    return getAwsApiHost(app, fallback);
}

export function getCFBHostForManageCustomFieldsLink(): string {
    const app = 'field-builder';
    return getAwsApiHost(app, `${app}.${legacyRndDomain}`);
}

export function getCrosstalkApiHost(): string {
    const app = 'crosstalk';
    return getAwsApiHost(app, `${app}.${legacyRndDomain}`);
}

export function getCrosstalkManagementLocation(): string {
    return `https://${getCrosstalkApiHost()}`;
}

export function getWebauthApiHost(): string {
    const app = 'webauth';
    return getAwsApiHost(app, `${app}.${legacyRndDomain}`);
}

function getAwsApiHost(app: string, fallbackHostname: string): string {
    const hostname = LocationHelper.getHostname();
    const pathname = LocationHelper.getPathname();

    if (hostname.includes(legacyUatDomain) || hostname.includes(uatDomain)) {
        const domain = hostname.includes(legacyUatDomain) ? legacyUatDomain : uatDomain;
        return (pathname.includes('/ddv-edge/') || hostname.includes('poc-rnd')) ?
            `${app}.poc-rnd.${domain}` :
            `${app}.app.${domain}`;
    } else if (hostname.includes(legacyProdDomain) || hostname.includes(prodDomain)) {
        const domain = hostname.includes(legacyProdDomain) ? legacyProdDomain : prodDomain;
        return hostname.includes('app-tst') ?
            `${app}.app-tst.${domain}` :
            `${app}.app.${domain}`;
    }

    return fallbackHostname;
}
